/**
 * Subdomain Detection Utility
 * Handles organization portal subdomain detection for both production and local development
 */

// Reserved subdomains that should not be treated as organization slugs
const RESERVED_SUBDOMAINS = ['www', 'app', 'api', 'admin', 'staging', 'dev', 'mail', 'ftp'];

// Production domain
const PRODUCTION_DOMAIN = 'cloudmortgage.io';

/**
 * Extract the organization slug from the current URL
 * Supports both production (*.cloudmortgage.io) and local development (*.localhost)
 *
 * @returns {string|null} The organization slug, or null if not on an org portal
 */
export const getOrgSlugFromUrl = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    const hostname = window.location.hostname;

    // Production pattern: {slug}.cloudmortgage.io
    const prodMatch = hostname.match(/^([a-z0-9-]+)\.cloudmortgage\.io$/i);
    if (prodMatch) {
        const slug = prodMatch[1].toLowerCase();
        if (!RESERVED_SUBDOMAINS.includes(slug)) {
            return slug;
        }
        return null;
    }

    // Local development pattern: {slug}.localhost
    const localMatch = hostname.match(/^([a-z0-9-]+)\.localhost$/i);
    if (localMatch) {
        const slug = localMatch[1].toLowerCase();
        if (!RESERVED_SUBDOMAINS.includes(slug)) {
            return slug;
        }
        return null;
    }

    return null;
};

/**
 * Check if the current URL is an organization portal
 *
 * @returns {boolean} True if on an org portal, false otherwise
 */
export const isOrgPortal = () => {
    return getOrgSlugFromUrl() !== null;
};

/**
 * Get the main site URL (without subdomain)
 *
 * @returns {string} The main site URL
 */
export const getMainSiteUrl = () => {
    if (typeof window === 'undefined') {
        return `https://${PRODUCTION_DOMAIN}`;
    }

    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;

    // Local development
    if (hostname.endsWith('.localhost') || hostname === 'localhost') {
        return `${protocol}//localhost${port ? `:${port}` : ''}`;
    }

    // Production
    return `${protocol}//${PRODUCTION_DOMAIN}`;
};

/**
 * Build an organization portal URL
 *
 * @param {string} slug - The organization slug
 * @returns {string} The full org portal URL
 */
export const getOrgPortalUrl = (slug) => {
    if (typeof window === 'undefined') {
        return `https://${slug}.${PRODUCTION_DOMAIN}`;
    }

    const protocol = window.location.protocol;
    const port = window.location.port;
    const hostname = window.location.hostname;

    // Local development
    if (hostname.endsWith('.localhost') || hostname === 'localhost') {
        return `${protocol}//${slug}.localhost${port ? `:${port}` : ''}`;
    }

    // Production
    return `${protocol}//${slug}.${PRODUCTION_DOMAIN}`;
};

export default {
    getOrgSlugFromUrl,
    isOrgPortal,
    getMainSiteUrl,
    getOrgPortalUrl,
    RESERVED_SUBDOMAINS,
};
